<template>
  <div class="crud_form_wrapper single_show_content_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.financialReportDetails") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #f7941e">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <!-- Start:: Loading State -->
      <div v-if="loading" class="loading_wrapper">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <!-- End:: Loading State -->

      <!-- Start:: Content -->
      <div class="row" v-else>
        <!-- ==== Start:: User Data Section ==== -->
        <div class="data_section">
          <h4 class="section_title">
            <i class="fal fa-user"></i>
            {{ $t("TITLES.userData") }}
          </h4>
          <div class="row">
            <!-- User Image -->
            <div
              class="col-12 col-md-3 text-center mb-4"
              v-if="data.user && data.user.image"
            >
              <img
                :src="data.user.image"
                :alt="data.user.name"
                class="user_avatar"
              />
            </div>

            <div
              :class="
                data.user && data.user.image ? 'col-12 col-md-9' : 'col-12'
              "
            >
              <div class="row">
                <!-- User Name -->
                <base-input
                  col="6"
                  type="text"
                  :placeholder="$t('PLACEHOLDERS.fullName')"
                  :value="data.user_name"
                  readonly
                />

                <!-- Mobile Number -->
                <base-input
                  col="6"
                  type="text"
                  :placeholder="$t('PLACEHOLDERS.mobileNumber')"
                  :value="data.mobile_number"
                  readonly
                />

                <!-- National ID / Commercial Registration -->
                <base-input
                  v-if="data.user && data.user.nationalId"
                  col="6"
                  type="text"
                  :placeholder="
                    getUserTypeText(data.user_type) ===
                    $t('PLACEHOLDERS.individual')
                      ? $t('PLACEHOLDERS.nationalId')
                      : $t('PLACEHOLDERS.commercialRegistration')
                  "
                  :value="data.user.nationalId"
                  readonly
                />

                <!-- User Type -->
                <base-input
                  col="6"
                  type="text"
                  :placeholder="$t('PLACEHOLDERS.userType')"
                  :value="getUserTypeText(data.user_type)"
                  readonly
                />

                <!-- Email -->
                <base-input
                  v-if="data.user && data.user.email"
                  col="6"
                  type="text"
                  :placeholder="$t('PLACEHOLDERS.email')"
                  :value="data.user.email"
                  readonly
                />
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: User Data Section ==== -->

        <!-- ==== Start:: Transaction Data Section ==== -->
        <div class="data_section mt-5">
          <h4 class="section_title">
            <i class="fal fa-receipt"></i>
            {{ $t("TITLES.transactionData") }}
          </h4>
          <div class="row">
            <!-- Transaction Type -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.transactionType')"
              :value="data.transactionable_type"
              readonly
            />

            <!-- Package/Subscription Name -->
            <base-input
              v-if="data.package_name"
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.packageName')"
              :value="data.package_name"
              readonly
            />

            <!-- Payment Date -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.paymentDate')"
              :value="formatDate(data.transaction_date)"
              readonly
            />

            <!-- Payment Method -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.paymentMethod')"
              :value="getPaymentMethodText(data.payment_method)"
              readonly
            />

            <!-- Transaction ID -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.transactionId')"
              :value="
                data.transaction_id ? data.transaction_id.toString() : '-'
              "
              readonly
            />

            <!-- Transaction Status -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.status')"
              :value="$t('STATUS.success')"
              readonly
            />

            <!-- Subscription Period (if applicable) -->
            <template
              v-if="data.transactionable && data.transactionable.start_date"
            >
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.subscriptionStartDate')"
                :value="formatDate(data.transactionable.start_date)"
                readonly
              />

              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.subscriptionEndDate')"
                :value="formatDate(data.transactionable.end_date)"
                readonly
              />
            </template>
          </div>
        </div>
        <!-- ==== End:: Transaction Data Section ==== -->

        <!-- ==== Start:: Announcement Details Section (If Announcement Type) ==== -->
        <div
          class="data_section mt-5"
          v-if="
            data.transactionable_type === 'Announcement' && data.transactionable
          "
        >
          <h4 class="section_title">
            <i class="fal fa-bullhorn"></i>
            {{ $t("TITLES.announcementDetails") }}
          </h4>
          <div class="row">
            <!-- Announcement Name -->
            <base-input
              v-if="data.transactionable.name"
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.announcementName')"
              :value="data.transactionable.name"
              readonly
            />

            <!-- Announcement Status -->
            <base-input
              v-if="data.transactionable.status"
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.announcementStatus')"
              :value="getAnnouncementStatusText(data.transactionable.status)"
              readonly
            />

            <!-- City -->
            <base-input
              v-if="data.transactionable.city_id"
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.city')"
              :value="data.transactionable.city_id"
              readonly
            />

            <!-- Address -->
            <base-input
              v-if="data.transactionable.address"
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.address')"
              :value="data.transactionable.address"
              readonly
            />

            <!-- Hospitality -->
            <base-input
              v-if="data.transactionable.Hospitality"
              col="12"
              type="text"
              :placeholder="$t('PLACEHOLDERS.Hospitality')"
              :value="data.transactionable.Hospitality"
              readonly
            />

            <!-- Notes -->
            <base-input
              v-if="data.transactionable.notes"
              col="12"
              type="text"
              :placeholder="$t('PLACEHOLDERS.notes')"
              :value="data.transactionable.notes"
              readonly
            />

            <!-- Announcement Images -->
            <div
              class="col-12"
              v-if="
                data.transactionable.images &&
                data.transactionable.images.length > 0
              "
            >
              <label class="d-block mb-2">{{
                $t("PLACEHOLDERS.announcementImages")
              }}</label>
              <div class="announcement_images">
                <img
                  v-for="(image, index) in data.transactionable.images"
                  :key="index"
                  :src="image"
                  :alt="'Announcement Image ' + (index + 1)"
                  class="announcement_image"
                  @click="showImageModal(image)"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: Announcement Details Section ==== -->

        <!-- ==== Start:: Financial Data Section ==== -->
        <div class="data_section mt-5">
          <h4 class="section_title">
            <i class="fal fa-coins"></i>
            {{ $t("TITLES.financialData") }}
          </h4>
          <div class="row">
            <!-- Original Price -->
            <div class="col-12 col-md-6">
              <div class="financial_card primary_card">
                <div class="card_icon">
                  <i class="fal fa-money-bill-wave"></i>
                </div>
                <div class="card_content">
                  <h6>{{ $t("PLACEHOLDERS.originalPrice") }}</h6>
                  <h3>
                    {{ data.original_price }} {{ $t("PLACEHOLDERS.sar") }}
                  </h3>
                </div>
              </div>
            </div>

            <!-- Tax Value -->
            <div class="col-12 col-md-6">
              <div class="financial_card warning_card">
                <div class="card_icon">
                  <i class="fal fa-percent"></i>
                </div>
                <div class="card_content">
                  <h6>{{ $t("PLACEHOLDERS.taxValue") }}</h6>
                  <h3>{{ data.tax_in_value }} {{ $t("PLACEHOLDERS.sar") }}</h3>
                </div>
              </div>
            </div>

            <!-- Total Price -->
            <div class="col-12 col-md-6">
              <div class="financial_card success_card">
                <div class="card_icon">
                  <i class="fal fa-money-check-alt"></i>
                </div>
                <div class="card_content">
                  <h6>{{ $t("PLACEHOLDERS.totalPriceWithTax") }}</h6>
                  <h3>{{ data.total_price }} {{ $t("PLACEHOLDERS.sar") }}</h3>
                </div>
              </div>
            </div>

            <!-- System Profit -->
            <div class="col-12 col-md-6">
              <div class="financial_card info_card">
                <div class="card_icon">
                  <i class="fal fa-chart-line"></i>
                </div>
                <div class="card_content">
                  <h6>{{ $t("PLACEHOLDERS.systemProfit") }}</h6>
                  <h3>{{ data.system_profit }} {{ $t("PLACEHOLDERS.sar") }}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: Financial Data Section ==== -->

        <!-- ==== Start:: Actions Section ==== -->
        <div class="actions_section mt-5">
          <div class="row justify-content-center">
            <div class="col-12 col-md-6">
              <base-button
                class="w-100"
                styleType="solid_btn"
                :btnText="$t('BUTTONS.downloadTaxInvoice')"
                @fireClick="downloadTaxInvoice"
                :isLoading="isDownloadingInvoice"
              >
                <template v-slot:btn_icon>
                  <i class="fal fa-file-invoice"></i>
                </template>
              </base-button>
            </div>
          </div>
        </div>
        <!-- ==== End:: Actions Section ==== -->
      </div>
      <!-- End:: Content -->
    </div>
    <!-- End:: Single Step Form Content -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ShowFinancialReport",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      loading: false,
      isDownloadingInvoice: false,
      id: null,
      data: {
        id: null,
        user_id: null,
        user_name: null,
        mobile_number: null,
        user_type: null,
        transactionable_type: null,
        transactionable_id: null,
        original_price: null,
        tax_in_value: null,
        total_price: null,
        system_profit: null,
        status: null,
        transaction_date: null,
        payment_method: null,
        transaction_id: null,
        package_name: null,
        user: null,
        transactionable: null,
      },
    };
  },

  methods: {
    async getData() {
      this.loading = true;

      try {
        const res = await this.$axios({
          method: "GET",
          url: `financial_reports/show/${this.id}`,
        });

        this.loading = false;
        this.data = res.data.data.Finacial;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },

    getUserTypeText(type) {
      if (type === "individual") {
        return this.$t("PLACEHOLDERS.individual");
      } else if (type === "store") {
        return this.$t("PLACEHOLDERS.store");
      }
      return "-";
    },

    getTransactionTypeText(type) {
      if (type === "UserSubscription") {
        return this.$t("PLACEHOLDERS.appSubscription");
      } else if (type === "Announcement") {
        return this.$t("PLACEHOLDERS.adPackage");
      }
      return "-";
    },

    getPaymentMethodText(method) {
      if (method === "online") {
        return this.$t("PLACEHOLDERS.onlinePayment");
      } else if (method === "wallet") {
        return this.$t("PLACEHOLDERS.wallet");
      }
      return "-";
    },

    getAnnouncementStatusText(status) {
      if (status === "published") {
        return this.$t("PLACEHOLDERS.published");
      } else if (status === "expired") {
        return this.$t("PLACEHOLDERS.expired");
      } else if (status === "pending") {
        return this.$t("STATUS.pending");
      }
      return "-";
    },

    showImageModal(image) {
      // Open image in new tab or show in modal
      window.open(image, "_blank");
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString(
        this.getAppLocale === "ar" ? "ar-SA" : "en-US",
        {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    },

    async downloadTaxInvoice() {
      this.isDownloadingInvoice = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: `financial-transactions/${this.id}/invoice`,
        });

        if (res.data.invoice_url) {
          window.open(res.data.invoice_url, "_blank");
        } else {
          this.$message.error(this.$t("MESSAGES.errorOccurred"));
        }
        this.isDownloadingInvoice = false;
      } catch (error) {
        this.isDownloadingInvoice = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
  },

  created() {
    this.id = this.$route.params.id;
    this.getData();
  },
};
</script>

<style scoped lang="scss">
.user_avatar {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--main_theme_clr);
}

.data_section {
  padding: 30px;
  border-radius: 12px;

  .section_title {
    color: var(--main_theme_clr);
    font-weight: bold;
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 2px solid #f0f0f0;

    i {
      margin-inline-end: 10px;
    }
  }
}

.financial_card {
  background: white;
  padding: 25px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  .card_icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: white;
  }

  .card_content {
    flex: 1;

    h6 {
      margin: 0;
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    }

    h3 {
      margin: 0;
      font-size: 24px;
      font-weight: bold;
      color: #333;
    }
  }

  &.primary_card .card_icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  &.warning_card .card_icon {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  }

  &.success_card .card_icon {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  }

  &.info_card .card_icon {
    background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  }
}

.actions_section {
  padding: 20px 0;
}

.loading_wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.announcement_images {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.announcement_image {
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}
</style>
